import { db, withSyncWrite } from '../db/indexeddb'
import { markAsSynced, markAsFailed } from './sync-queue'
import type Dexie from 'dexie'

/**
 * Generic Push Engine
 * Pushes local entity changes to cloud using a descriptor-based approach.
 */

/** Configuration for pushing a specific entity type to cloud. */
export interface PushDescriptor<TLocal> {
  entityName: string

  // Local DB
  getLocal: (localId: string) => Promise<TLocal | null | undefined>
  getLocalCloudId: (entity: TLocal) => string | null
  getLocalId: (entity: TLocal) => string

  // Payload builders
  buildCreatePayload: (entity: TLocal, userId: string) => Promise<Record<string, any>>
  buildUpdatePayload: (entity: TLocal) => Promise<Record<string, any>>

  // Cloud operations
  createCloud: (userId: string, payload: any) => Promise<{ id: string; created_at: string } | null>
  updateCloud: (cloudId: string, payload: any, userId: string) => Promise<{ updated_at: string } | null>
  deleteCloud: (cloudId: string, userId: string) => Promise<boolean>

  // Dexie Table Reference
  table: Dexie.Table

  // Optional: Pre-push validation (e.g. "project not synced yet → defer")
  canCreate?: (entity: TLocal) => Promise<{ defer: boolean }>
  canUpdate?: (entity: TLocal) => Promise<{ defer: boolean }>
}

export interface PushResult {
  created: number
  updated: number
  deleted: number
  errors: string[]
}

/**
 * Push a single entity change to cloud.
 */
export async function pushEntityChange<TLocal>(
  change: any,
  userId: string,
  result: PushResult,
  descriptor: PushDescriptor<TLocal>,
): Promise<void> {
  switch (change.action) {
    case 'create': {
      const entity = await descriptor.getLocal(change.data?.localId ?? '')
      if (!entity) {
        // Entity was deleted locally — discard stale queue entry
        await markAsSynced(change.id!)
        break
      }

      // Skip if already linked to cloud (avoids duplicate push)
      if (descriptor.getLocalCloudId(entity)) {
        await markAsSynced(change.id!)
        break
      }

      // Check pre-push validation (e.g. parent project not synced yet)
      if (descriptor.canCreate) {
        const { defer } = await descriptor.canCreate(entity)
        if (defer) break
      }

      const payload = await descriptor.buildCreatePayload(entity, userId)
      const cloudResult = await descriptor.createCloud(userId, payload)

      if (cloudResult) {
        await withSyncWrite(() =>
          descriptor.table.update(descriptor.getLocalId(entity), {
            userId,
            cloudId: cloudResult.id,
            synced: true,
            syncedAt: new Date(),
            updatedAt: new Date(cloudResult.created_at),
          })
        )
        await markAsSynced(change.id!)
        result.created++
      } else {
        await markAsFailed(change.id!, `create${descriptor.entityName} returned null`)
      }
      break
    }

    case 'update': {
      const entity = await descriptor.getLocal(change.data?.localId ?? '')
      if (!entity) {
        await markAsSynced(change.id!)
        break
      }

      const cloudId = descriptor.getLocalCloudId(entity)
      if (!cloudId) {
        // Entity hasn't been pushed to cloud yet — defer to next cycle
        break
      }

      // Check pre-push validation
      if (descriptor.canUpdate) {
        const { defer } = await descriptor.canUpdate(entity)
        if (defer) break
      }

      const payload = await descriptor.buildUpdatePayload(entity)
      console.log(`[Sync] PUSH ${descriptor.entityName} update: cloudId=${cloudId}`)
      const updateResult = await descriptor.updateCloud(cloudId, payload, userId)

      if (!updateResult) {
        result.errors.push(`update${descriptor.entityName} failed for ${cloudId}`)
        await markAsFailed(change.id!, `update${descriptor.entityName} returned null`)
        break
      }

      await withSyncWrite(() =>
        descriptor.table.update(descriptor.getLocalId(entity), {
          synced: true,
          syncedAt: new Date(),
          updatedAt: new Date(updateResult.updated_at),
        })
      )
      await markAsSynced(change.id!)
      result.updated++
      break
    }

    case 'delete': {
      const entity = await descriptor.getLocal(change.data?.localId ?? '')
      const cloudId = (entity ? descriptor.getLocalCloudId(entity) : null) ?? change.data?.cloudId
      if (cloudId) {
        const success = await descriptor.deleteCloud(cloudId, userId)
        if (!success) {
          result.errors.push(`delete${descriptor.entityName} failed for ${cloudId}`)
          await markAsFailed(change.id!, `delete${descriptor.entityName} returned false`)
          break
        }
        result.deleted++
      }
      // Mark as synced — if no cloudId, the entity was never uploaded
      await markAsSynced(change.id!)
      break
    }
  }
}
